/**
 * 前端 Mock 模块
 * 拦截所有 axios 请求并返回模拟数据，无需后端服务即可运行整个系统
 * 开启方式：.env.development 中设置 VITE_APP_MOCK = 'true'
 * 配合 src/settings.js 的 frontRouter: true（前端静态路由）使用
 */
import frontRoutes from '@/router/front'

// 模拟网络延迟（毫秒）
const MOCK_DELAY = 300

// -------------------- 基础工具 --------------------
const surnames = '张王李赵刘陈杨黄周吴徐孙马朱胡郭何林罗郑梁谢宋唐许韩冯邓曹彭'
const givenNames = '伟芳娜敏静丽强磊军洋勇艳杰娟涛明超秀兰霞平刚桂英华玉浩宇欣怡子轩雨桐'
const words = ['管理', '系统', '测试', '示例', '数据', '演示', '默认', '常用', '基础', '业务']

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min
}

function pick(arr) {
  return arr[randInt(0, arr.length - 1)]
}

function pad(n) {
  return n < 10 ? '0' + n : '' + n
}

// 随机中文姓名
function chineseName() {
  let name = pick(surnames.split(''))
  const len = randInt(1, 2)
  for (let i = 0; i < len; i++) {
    name += pick(givenNames.split(''))
  }
  return name
}

// 随机时间（近一年内）
function datetime() {
  const now = Date.now()
  const t = new Date(now - randInt(0, 365 * 24 * 3600 * 1000))
  return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())} ${pad(t.getHours())}:${pad(t.getMinutes())}:${pad(t.getSeconds())}`
}

function phone() {
  return '1' + pick(['3', '5', '7', '8', '9']) + String(randInt(100000000, 999999999))
}

// 成功响应体
function ok(data = {}, msg = '操作成功') {
  return { code: 200, msg: msg, data: data }
}

// 模拟延迟返回 axios 响应
function respond(config, body) {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        data: body,
        status: 200,
        statusText: 'OK',
        headers: {},
        config: config
      })
    }, MOCK_DELAY)
  })
}

// -------------------- 通用数据生成 --------------------
// 通用记录：尽量覆盖各列表页/详情页常用字段
function mockRecord(id) {
  const name = chineseName()
  return {
    id: id,
    pid: 0,
    name: pick(words) + ' ' + id,
    title: pick(words) + '标题' + id,
    username: 'user' + id,
    nickname: name,
    real_name: name,
    phone: phone(),
    email: `user${id}@example.com`,
    avatar: '',
    avatar_url: '',
    images: [],
    image: '',
    file_url: '',
    url: '',
    link: '',
    icon: '',
    content: '这是一段示例内容，用于前端 mock 演示。',
    description: '这是一段示例描述，用于前端 mock 演示。',
    remark: '示例备注信息',
    intro: '示例简介',
    type: randInt(1, 3),
    type_name: pick(['类型一', '类型二', '类型三']),
    status: randInt(0, 1),
    is_disable: randInt(0, 1),
    is_show: randInt(0, 1),
    is_top: 0,
    is_default: 0,
    sort: id,
    num: randInt(0, 1000),
    count: randInt(0, 1000),
    hits: randInt(0, 10000),
    price: randInt(1, 9999) / 100,
    score: randInt(0, 100),
    ip: `192.168.${randInt(0, 255)}.${randInt(1, 254)}`,
    region: pick(['广东省 深圳市', '北京市 朝阳区', '上海市 浦东新区', '浙江省 杭州市', '四川省 成都市']),
    address: '示例地址' + id + '号',
    start_time: datetime(),
    end_time: datetime(),
    login_time: datetime(),
    create_time: datetime(),
    update_time: datetime(),
    delete_time: null,
    children: []
  }
}

// 通用分页列表
function mockList(config) {
  const params = { ...(config.params || {}), ...(config.data || {}) }
  const limit = Number(params.limit) || 15
  const page = Number(params.page) || 1
  const count = 47
  const list = []
  const start = (page - 1) * limit
  for (let i = 1; i <= Math.min(limit, count - start); i++) {
    list.push(mockRecord(start + i))
  }
  return ok({
    list: list,
    count: count,
    total: count,
    page: page,
    limit: limit,
    // 各列表页下拉选项等基础数据
    basedata: {
      exps: [],
      depts: mockOptions('部门'),
      posts: mockOptions('岗位'),
      roles: mockOptions('角色'),
      groups: mockOptions('分组'),
      tags: mockOptions('标签'),
      categorys: mockOptions('分类'),
      members: [],
      users: [],
      menus: [],
      types: mockOptions('类型')
    }
  })
}

function mockOptions(prefix) {
  return [1, 2, 3].map((i) => ({
    id: i,
    value: i,
    label: `${prefix}${i}`,
    name: `${prefix}${i}`,
    title: `${prefix}${i}`
  }))
}

// 通用详情
function mockInfo(config) {
  const params = { ...(config.params || {}), ...(config.data || {}) }
  const id = Number(params.id) || 1
  return ok({ ...mockRecord(id), basedata: mockList(config).data.basedata })
}

// 通用统计数字
function mockCount() {
  const data = {}
  const keys = ['member', 'content', 'file', 'visit', 'order', 'message', 'user', 'today', 'total']
  keys.forEach((k) => {
    data[k] = randInt(10, 9999)
    data[k + '_count'] = randInt(10, 9999)
    data[k + '_today'] = randInt(0, 100)
  })
  return ok(data)
}

// -------------------- 特殊接口 --------------------
// 收集前端静态路由中的全部权限标识，作为 mock 用户的角色权限
function collectRoles(routes, acc = new Set()) {
  routes.forEach((route) => {
    const roles = route.meta?.roles
    if (Array.isArray(roles)) {
      roles.forEach((r) => acc.add(r))
    }
    if (route.children) {
      collectRoles(route.children, acc)
    }
  })
  return acc
}

// 系统设置（登录页/个人中心通用）
function mockSetting() {
  return {
    system_name: 'yylAdmin',
    page_title: '',
    favicon_url: '',
    logo_url: '',
    login_bg_url: '',
    login_bg_color: '',
    login_sn_color: '',
    is_watermark: 0,
    watermark_content: '',
    api_timeout: 60,
    token_type: 'header',
    token_name: 'AdminToken',
    captcha_switch: 0,
    captcha_mode: 1,
    captcha_type: 0,
    captcha_img: '',
    register_switch: 0
  }
}

// 特殊接口处理：返回 null 表示未命中，走通用处理
function specialHandle(url, config) {
  // 登录
  if (url.endsWith('system.Login/login')) {
    return ok({ AdminToken: 'mock-token-' + Date.now() }, '登录成功')
  }
  // 退出
  if (url.endsWith('system.Login/logout')) {
    return ok({}, '退出成功')
  }
  // 验证码：关闭验证码
  if (url.endsWith('system.Login/captcha')) {
    return ok({ captcha_switch: 0, captcha_mode: 1, captcha_type: 0, captcha_img: '', captcha_id: '' })
  }
  // 系统设置
  if (url.endsWith('system.Login/setting')) {
    return ok(mockSetting())
  }
  // 个人中心-用户信息
  if (url.endsWith('system.UserCenter/info')) {
    return ok({
      user_id: 1,
      username: 'admin',
      nickname: '超级管理员',
      avatar_url: '',
      roles: [...collectRoles(frontRoutes)],
      menus: [],
      setting: mockSetting()
    })
  }
  // 首页统计
  if (/system\.Index\/(index|count|member|content|file)$/.test(url)) {
    return mockCount()
  }
  // 首页公告
  if (url.endsWith('system.Index/notice')) {
    return ok({ list: [mockRecord(1), mockRecord(2)], count: 2 })
  }
  return null
}

// -------------------- 通用分发 --------------------
function mockHandle(config) {
  // 完整路径（去掉 baseURL）
  const url = (config.url || '').replace(config.baseURL || '', '')

  // 文件下载（导出等）
  if (config.responseType === 'blob') {
    return respond(config, new Blob(['mock file'], { type: 'text/plain' }))
  }

  // 特殊接口
  const special = specialHandle(url, config)
  if (special) {
    return respond(config, special)
  }

  // 动作名：url 最后一段，如 /admin/system.User/list -> list
  const action = url.split('/').pop() || ''
  const actionLower = action.toLowerCase()

  // 列表：list、logList、messageList 等
  if (actionLower.endsWith('list')) {
    return respond(config, mockList(config))
  }
  // 详情：info、logInfo、messageInfo 等
  if (actionLower.endsWith('info')) {
    return respond(config, mockInfo(config))
  }
  // 统计：statistic、count 等
  if (actionLower.includes('statistic') || actionLower.includes('count')) {
    return respond(config, mockCount())
  }
  // 添加/修改的 get 请求：返回表单默认数据
  if ((actionLower === 'add' || actionLower === 'edit') && config.method === 'get') {
    return respond(config, mockInfo(config))
  }
  // 其余（添加、修改、删除、禁用、清空等提交操作）：直接成功
  return respond(config, ok())
}

/**
 * axios 自定义适配器：拦截所有请求并返回 mock 数据
 * @param {object} config axios 请求配置
 */
export function mockAdapter(config) {
  if (import.meta.env.DEV) {
    console.log('[mock]', config.method?.toUpperCase(), config.url, config.params || config.data || '')
  }
  return mockHandle(config)
}
